/**
 * Grid Banner mobile slider — auto-swipe + seamless infinite loop (both ways).
 *
 * The Grid Banner section uses the theme's native scroll-snap `swipe-mobile`
 * track (no JS of its own). On mobile this controller adds:
 *   - autoplay with a merchant-controlled interval (data-autoplay-interval, ms)
 *   - an infinite loop in BOTH directions: clones of the last banners are
 *     placed before the first, and clones of the first after the last, so a
 *     swipe (or autoplay) can carry on past either end. Whenever a scroll
 *     settles onto a clone, we jump instantly to the identical real banner —
 *     an invisible reset that keeps the native swipe feel while looping.
 *
 * It stays idle on desktop, when the tab is hidden, when the slider is out of
 * view, while the shopper is interacting, and when reduced motion is requested.
 */
(function () {
  'use strict';

  const MOBILE_MQ = '(max-width: 767.98px)';
  const REDUCE_MQ = '(prefers-reduced-motion: reduce)';
  const SELECTOR = '.grid-banner[data-grid-banner-autoplay="true"]';
  // 90vw banners show one + a peek. With `scroll-snap-stop: always` each flick
  // advances one banner, so N clones each side absorb N rapid flicks before the
  // settle handler folds them back. Three covers bursty swiping; the clone count
  // is capped at 2 x this regardless of how many banners the merchant adds.
  const CLONES_PER_SIDE = 3;

  class GridBannerSlider {
    constructor(root) {
      this.root = root; // the scroll container (.grid-banner.swipe-mobile)
      this.track = root.querySelector('.swipe-mobile__inner');
      if (!this.track) return;

      this.interval = Math.max(1000, parseInt(root.dataset.autoplayInterval, 10) || 4000);
      this.mobileMql = window.matchMedia(MOBILE_MQ);
      this.reduceMql = window.matchMedia(REDUCE_MQ);

      this.timer = null;
      this.resumeTimer = null;
      this.settleTimer = null;
      this.suppressTimer = null;
      this.clones = null;
      this.prepend = 0; // number of clones sitting before the first real banner
      this.suppressSettle = false; // ignore scroll events caused by our own repositioning
      this.state = {
        mobile: false,
        visible: !document.hidden,
        inView: true,
        interacting: false,
        pointerDown: false,
      };

      this.tick = this.tick.bind(this);
      this.sync = this.sync.bind(this);
      this.onMediaChange = this.onMediaChange.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onInteractStart = this.onInteractStart.bind(this);
      this.onInteractEnd = this.onInteractEnd.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onSettle = this.onSettle.bind(this);

      this.addListeners();

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            this.state.inView = entries[0].isIntersecting;
            this.sync();
          },
          { threshold: 0.25 }
        );
        this.observer.observe(root);
      }

      this.onMediaChange();
    }

    // Every banner in the track, in DOM order, including the loop clones.
    allSlides() {
      return Array.prototype.filter.call(this.track.children, (el) =>
        el.classList.contains('grid-banner__item')
      );
    }

    // Real (author-configured) banners only.
    realSlides() {
      return this.allSlides().filter((el) => !el.classList.contains('grid-banner__item--clone'));
    }

    addListeners() {
      this.mobileMql.addEventListener('change', this.onMediaChange);
      this.reduceMql.addEventListener('change', this.onMediaChange);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.root.addEventListener('scroll', this.onScroll, { passive: true });
      ['pointerdown', 'touchstart'].forEach((ev) =>
        this.root.addEventListener(ev, this.onInteractStart, { passive: true })
      );
      ['pointerup', 'pointercancel', 'touchend', 'touchcancel'].forEach((ev) =>
        this.root.addEventListener(ev, this.onInteractEnd, { passive: true })
      );
    }

    destroy() {
      this.stop();
      [this.resumeTimer, this.settleTimer, this.suppressTimer].forEach((t) => t && clearTimeout(t));
      this.root.style.scrollSnapType = '';
      this.root.style.scrollBehavior = '';
      this.mobileMql.removeEventListener('change', this.onMediaChange);
      this.reduceMql.removeEventListener('change', this.onMediaChange);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.root.removeEventListener('scroll', this.onScroll);
      if (this.observer) this.observer.disconnect();
      this.removeClones();
    }

    onMediaChange() {
      this.state.mobile = this.mobileMql.matches;
      // Clones power the loop for BOTH swipe and autoplay, so they exist whenever
      // the mobile slider is active (autoplay itself is gated separately below).
      if (this.state.mobile) {
        this.addClones();
        this.goToIndex(this.prepend); // start on the real first banner, past the leading clones
      } else {
        this.removeClones();
      }
      this.sync();
    }

    onVisibility() {
      this.state.visible = !document.hidden;
      this.sync();
    }

    onInteractStart() {
      this.state.interacting = true;
      this.state.pointerDown = true;
      if (this.resumeTimer) {
        clearTimeout(this.resumeTimer);
        this.resumeTimer = null;
      }
      this.stop();
    }

    onInteractEnd() {
      this.state.pointerDown = false;
      if (this.resumeTimer) clearTimeout(this.resumeTimer);
      // Wait a beat so a manual swipe isn't immediately overridden by autoplay.
      this.resumeTimer = setTimeout(() => {
        this.state.interacting = false;
        this.sync();
      }, Math.min(this.interval, 2500));
      // Re-check the landing once the gesture is over (covers drags with no momentum).
      this.scheduleSettle();
    }

    onScroll() {
      if (this.suppressSettle) return;
      this.scheduleSettle();
    }

    scheduleSettle() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.onSettle, 120);
    }

    onSettle() {
      if (this.state.pointerDown) return; // don't jump the content out from under a finger
      this.reposition();
    }

    canPlay() {
      const s = this.state;
      return (
        s.mobile &&
        s.visible &&
        s.inView &&
        !s.interacting &&
        !this.reduceMql.matches &&
        this.realSlides().length > 1
      );
    }

    sync() {
      if (this.canPlay()) this.start();
      else this.stop();
    }

    start() {
      if (this.timer) return;
      this.timer = setInterval(this.tick, this.interval);
    }

    stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    makeClone(slide) {
      const clone = slide.cloneNode(true);
      clone.classList.add('grid-banner__item--clone');
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input, [tabindex]').forEach((el) =>
        el.setAttribute('tabindex', '-1')
      );
      return clone;
    }

    addClones() {
      const reals = this.realSlides();
      const n = reals.length;
      if (this.clones || n < 2) return;

      const per = Math.min(CLONES_PER_SIDE, n);
      this.prepend = per;
      this.clones = [];

      // Before the first banner: clones of the LAST `per` banners, in order, so a
      // backward swipe from banner 1 lands on banner N (and its correct peek).
      const lead = document.createDocumentFragment();
      for (let k = n - per; k < n; k++) {
        const clone = this.makeClone(reals[k]);
        lead.appendChild(clone);
        this.clones.push(clone);
      }
      this.track.insertBefore(lead, reals[0]);

      // After the last banner: clones of the FIRST `per` banners, so a forward
      // swipe (or the autoplay wrap) from banner N lands on banner 1.
      for (let k = 0; k < per; k++) {
        const clone = this.makeClone(reals[k]);
        this.track.appendChild(clone);
        this.clones.push(clone);
      }
    }

    removeClones() {
      if (this.clones) {
        this.clones.forEach((clone) => clone.parentNode && clone.parentNode.removeChild(clone));
        this.clones = null;
      }
      this.prepend = 0;
    }

    // Viewport x-coordinate a banner's start edge lands on when snapped to start.
    // Computed once and reused across a whole scan to avoid repeated style reads.
    snapRef() {
      const pad = parseFloat(getComputedStyle(this.root).scrollPaddingLeft) || 0;
      return this.root.getBoundingClientRect().left + pad;
    }

    // How far a banner's start edge currently sits from the snap-start position.
    // 0 means that banner is snapped to the start. Live rects already include the
    // inter-banner gap, so nothing is left unaccounted for.
    snapDistance(slide, ref) {
      return slide.getBoundingClientRect().left - (ref === undefined ? this.snapRef() : ref);
    }

    // Track index of the banner currently snapped to the start (may be a clone).
    currentIndex() {
      const ref = this.snapRef(); // one style + layout read for the whole scan
      let best = 0;
      let bestDist = Infinity;
      this.allSlides().forEach((slide, i) => {
        const dist = Math.abs(this.snapDistance(slide, ref));
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      });
      return best;
    }

    scrollToIndex(i, smooth) {
      const slide = this.allSlides()[i];
      if (!slide) return;
      const left = Math.max(0, Math.round(this.root.scrollLeft + this.snapDistance(slide)));
      this.root.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
    }

    // Instant, snap-safe jump to a banner. `scroll-behavior` is neutralised so the
    // CSS `smooth` can't turn this into a visible slide, and snapping is momentarily
    // disabled so `scroll-snap-stop: always` can't strand a multi-banner jump on an
    // intermediate banner. It runs in one synchronous frame, so it never paints
    // mid-way — the reposition onto an identical clone/real banner is invisible.
    goToIndex(i) {
      const prevSnap = this.root.style.scrollSnapType;
      const prevBehavior = this.root.style.scrollBehavior;
      this.root.style.scrollSnapType = 'none';
      this.root.style.scrollBehavior = 'auto';
      this.scrollToIndex(i, false);
      void this.root.offsetHeight; // commit the jump before snapping is restored
      this.root.style.scrollBehavior = prevBehavior;
      this.root.style.scrollSnapType = prevSnap;
    }

    // If a scroll settled on a clone, fold it back onto the identical real banner
    // so the shopper can keep swiping in either direction forever.
    reposition() {
      const P = this.prepend;
      if (!P) return;
      const n = this.realSlides().length;
      const idx = this.currentIndex();

      let target = idx;
      if (idx < P) target = idx + n; // on a leading clone -> the matching real banner
      else if (idx >= P + n) target = idx - n; // on a trailing clone -> the matching real banner
      if (target === idx || !this.allSlides()[target]) return;

      this.suppressSettle = true;
      this.goToIndex(target);
      clearTimeout(this.suppressTimer);
      this.suppressTimer = setTimeout(() => {
        this.suppressSettle = false;
      }, 200);
    }

    tick() {
      const n = this.realSlides().length;
      if (n < 2) {
        this.stop();
        return;
      }

      const P = this.prepend;
      let current = this.currentIndex();
      // Make sure we advance from a real banner (fold back if a settle is still pending).
      if (current < P || current >= P + n) {
        this.reposition();
        current = this.currentIndex();
      }

      // Advance one banner. From the last real banner this lands on the leading
      // clone of banner 1; the settle handler then folds it back invisibly.
      this.scrollToIndex(current + 1, true);
    }
  }

  const initWithin = (scope) => {
    const container = scope && scope.querySelectorAll ? scope : document;
    container.querySelectorAll(SELECTOR).forEach((el) => {
      if (!el.gridBannerSlider) el.gridBannerSlider = new GridBannerSlider(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initWithin(document));
  } else {
    initWithin(document);
  }

  // Shopify theme editor lifecycle.
  document.addEventListener('shopify:section:load', (e) => initWithin(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll(SELECTOR).forEach((el) => {
      if (el.gridBannerSlider) {
        el.gridBannerSlider.destroy();
        el.gridBannerSlider = null;
      }
    });
  });
})();
